import { useEffect, useState } from "react";
import { API } from "@/lib/api";

/**
 * Loads property photos (curb shot + aerial w/ comp pins) from the
 * backend, returning blob object URLs that can be dropped into <img>.
 *
 * `endpointBase` is everything before the `/photos/{kind}` segment —
 * for example:
 *   • auth route:   `/leads/{id}/photos`           (sends JWT via fetch)
 *   • public route: `/public/report/{token}/photos`  (no auth)
 *
 * Pass `authToken` for the auth route, leave undefined for public.
 *
 * Returns `{ streetUrl, satelliteUrl, loading }` — `null` values mean
 * "not yet loaded", `""` means the backend returned 204 (no coverage)
 * so the caller can hide the slot.
 */
export function usePropertyPhotos({ endpointBase, authToken } = {}) {
  const [streetUrl, setStreetUrl] = useState(null);
  const [satelliteUrl, setSatelliteUrl] = useState(null);

  useEffect(() => {
    if (!endpointBase) return;
    const controller = new AbortController();
    const urlsToRevoke = [];

    const fetchKind = async (kind, setter) => {
      try {
        const res = await fetch(`${API}${endpointBase}/${kind}`, {
          signal: controller.signal,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (res.status === 204 || !res.ok) {
          setter("");
          return;
        }
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        urlsToRevoke.push(objUrl);
        setter(objUrl);
      } catch (err) {
        if (err.name !== "AbortError") setter("");
      }
    };

    fetchKind("street", setStreetUrl);
    fetchKind("satellite", setSatelliteUrl);

    return () => {
      controller.abort();
      urlsToRevoke.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [endpointBase, authToken]);

  return { streetUrl, satelliteUrl, loading: streetUrl === null || satelliteUrl === null };
}
